import { MultiSelect } from "@twinkltech/curriculum-architect-component-library";
import { ThreadSlug } from "@twinkltech/curriculum-architect-domain-hooks";
import { Button, Text } from "@twinkltech/web-ui";
import { useTranslation } from "react-i18next";

import { useUrlFilters } from "@/hooks/useUrlFilters/useUrlFilters";

import { Z_INDEX_ORDERS } from "@/pages/config/zIndexOrders.const";
import { EDIT_SUBJECT_PLAN_NS } from "@/pages/EditSubjectPlan/EditSubjectPlan.i18n";

import { TableResource } from "@/states/unitTableReducer/unitTableReducer";

import { ResourceHelper } from "@/utils/ResourceHelper/ResourceHelper";

import { COMMON_NS } from "@/i18n/common/common.i18n";

export const ResourceFilters = ({
  resources,
}: {
  resources: TableResource[];
}) => {
  const { t } = useTranslation(EDIT_SUBJECT_PLAN_NS);
  const { clearAllFilters, filterOptions, setFilterValue } = useUrlFilters({
    filterKeys: ["keyStages", "threads"],
  });

  const keyStageTagSet = new Set<string>(
    resources.flatMap((resource) => resource?.keyStagesSlug || [])
  );

  // Create options for key stages filter dropdown
  const keyStageOptions = Array.from(keyStageTagSet).map((keyStage) => ({
    value: keyStage,
    label: ResourceHelper.getKeyStageLabel(keyStage),
  }));

  const orderedKeyStageOptions = ResourceHelper.sortKeyStageOptions(keyStageOptions);

  const threadTagSet = new Set<ThreadSlug>();
  resources.forEach((resource) => {
    resource.threadSlugs?.forEach((thread) => threadTagSet.add(thread));
  });

  // Create options for threads filter dropdown
  const threadOptions = Array.from(threadTagSet).map((thread) => ({
    value: thread,
    label: t(`threads.${thread}`, { ns: COMMON_NS }),
  }));

  return (
    <div
      aria-labelledby="filter-units"
      className="flex mt-6 mb-10"
      role="search"
    >
      <div className="w-full flex gap-4 items-center grow-1">
        <Text
          as="span"
          className="sr-only"
          id="filter-units"
          variant="lg-medium"
        >
          {t("filter-units.title")}
        </Text>
        <div className="flex gap-3">
          {orderedKeyStageOptions.length > 0 && (
            <MultiSelect
              className={"w-[180px]"}
              label="Age Groups"
              options={orderedKeyStageOptions ?? []}
              value={filterOptions.keyStages || []}
              zindex={Z_INDEX_ORDERS.EDIT_SUBJECT_PLAN.FILTER_DROPDOWN}
              onChange={(value: string[]) => {
                setFilterValue("keyStages", value);
              }}
            />
          )}
          {threadOptions.length > 0 && (
            <MultiSelect
              className={"w-[180px]"}
              label="Threads"
              options={threadOptions ?? []}
              value={filterOptions.threads || []}
              zindex={Z_INDEX_ORDERS.EDIT_SUBJECT_PLAN.FILTER_DROPDOWN}
              onChange={(value: string[]) => {
                setFilterValue("threads", value);
              }}
            />
          )}
        </div>
        <Button
          as="button"
          className="text-[#1C7AC7] underline"
          size="sm"
          type="button"
          variant="link"
          onClick={clearAllFilters}
        >
          <Text variant="sm-normal">
            {t("actions.clear-filters", { ns: COMMON_NS })}
          </Text>
        </Button>
      </div>
    </div>
  );
};
